import { useState, useRef, useCallback, useEffect } from 'react';
import {
  multiFactor,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  PhoneAuthProvider,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import {
  updateDoc,
  setDoc,
  getDoc,
  arrayUnion,
  serverTimestamp,
} from 'firebase/firestore';
// logEvent also feeds the first-party stream via the GA4 bridge;
// the signature and GA4 behaviour are unchanged.
import { logEvent } from '../../analytics/ga4';
import { auth, analytics } from '../../firebase';
import { isNonProdEnvironment } from '../../utils/environmentTheme';
import { formatUKPhoneNumber, isValidUKPhone } from '../../utils/auth/phone';

/**
 * useMfaEnrollment
 * ================
 * Single source of truth for *enrolling* a new second factor (SMS or
 * authenticator-app / TOTP) during first-time MFA setup. It powers BOTH the
 * provider setup page (`SetUpMFA`) and the service-user setup page
 * (`PatientSetUpMFA`).
 *
 * Why this hook exists
 * --------------------
 * The two setup pages previously duplicated ~350 lines of near-identical,
 * security-sensitive enrollment logic: reCAPTCHA lifecycle, SMS send/resend with
 * a cooldown, code verification, TOTP secret generation, method switching, the
 * "shared test number" dev bypass, and the Firestore audit writes. Two copies
 * means two chances to drift on auth code. Centralising it here keeps the two
 * pages perfectly in parity and gives us one place to test and maintain.
 *
 * What stays in the pages (and why)
 * ---------------------------------
 * Everything that genuinely differs per caller is injected via config, so this
 * hook holds no page-specific policy:
 *   - `resolveDocRef` — where to persist MFA status (provider `clinicians`/RMS doc
 *     vs service-user `patients`/`informants` doc).
 *   - `onEnrolled`    — where to navigate after a successful enrollment (provider
 *     dashboard + RMS finalisation vs service-user welcome/dashboard).
 *   - `analytics`     — event-name prefix + whether to log failures, so existing
 *     analytics event names are preserved exactly for each product.
 *
 * The pages also own all presentation (intro copy, "Skip" button, sign-out
 * routing, environment banner). This hook only owns enrollment mechanics + state.
 *
 * Environment behaviour
 * ---------------------
 * `allowSms` gates whether SMS is offered at all (the provider page passes
 * `isNonProdEnvironment()` so production stays authenticator-only; the
 * service-user page passes `true` so both methods are always offered). The
 * "shared test number" bypass for `auth/credential-already-in-use` is ALWAYS
 * gated on `isNonProdEnvironment()` so it can never run on a production tenant —
 * including `production-slam-nhs`, which a naive `!== 'production'` check would
 * have wrongly treated as non-prod.
 *
 * @param {object}   config
 * @param {boolean}  [config.allowSms=false]      Offer SMS as a method (else TOTP only).
 * @param {'totp'|'sms'|null} [config.initialMethod=null] Method selected on mount
 *        (provider production passes 'totp' to skip the picker and auto-generate).
 * @param {Function} config.resolveDocRef  async () => ({ ref, snap } | null) — the
 *        Firestore doc to write MFA status to, plus its current snapshot.
 * @param {Function} config.onEnrolled     async () => void — navigate after success.
 * @param {object}   [config.analytics]
 * @param {string}   [config.analytics.accountType]  'Provider' | 'Service User' (for events).
 * @param {string}   [config.analytics.userType]     Firebase claim userType (for events).
 * @param {string}   [config.analytics.eventPrefix=''] Prefix for event names, e.g. 'patient_'.
 * @param {boolean}  [config.analytics.logFailures=false] Emit `${prefix}mfa_enrolment_failed`.
 */
export default function useMfaEnrollment({
  allowSms = false,
  initialMethod = null,
  resolveDocRef,
  onEnrolled,
  analytics: analyticsConfig = {},
} = {}) {
  const {
    accountType = '',
    userType = '',
    eventPrefix = '',
    logFailures = false,
  } = analyticsConfig;

  // ---------------------------------------------------------------------------
  // Method selection
  // ---------------------------------------------------------------------------
  // null = no method chosen yet (the page shows the method picker). Provider
  // production seeds this with 'totp' so it renders the authenticator panel
  // straight away with no picker.
  const [selectedMethod, setSelectedMethod] = useState(initialMethod);

  // ---------------------------------------------------------------------------
  // Shared error surface (both methods write here; the page renders one banner)
  // ---------------------------------------------------------------------------
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');

  // The 6-digit code the user types (SMS OTP or TOTP code — only one flow is
  // active at a time, so a single field is fine and keeps the UI simple).
  const [verificationCode, setVerificationCode] = useState('');

  // ---------------------------------------------------------------------------
  // SMS state
  // ---------------------------------------------------------------------------
  const [verificationId, setVerificationId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [last4, setLast4] = useState('');
  // Resend management (30s cooldown + "sent" flash)
  const cooldownSeconds = 30;
  const [sentAt, setSentAt] = useState(0);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendJustSent, setResendJustSent] = useState(false);
  const recaptchaVerifierRef = useRef(null);

  // ---------------------------------------------------------------------------
  // TOTP (authenticator app) state
  // ---------------------------------------------------------------------------
  const [totpSecret, setTotpSecret] = useState(null);
  const [totpUri, setTotpUri] = useState('');
  const [totpSecretKey, setTotpSecretKey] = useState('');
  const [isTotpGenerating, setIsTotpGenerating] = useState(false);
  const [isTotpReady, setIsTotpReady] = useState(false);
  const [isTotpEnrolling, setIsTotpEnrolling] = useState(false);
  const [isTotpError, setIsTotpError] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // ---------------------------------------------------------------------------
  // Panel height measurement — lets the page animate the enrollment panel's
  // height smoothly as content changes (choice -> SMS/TOTP, skeleton -> QR).
  // Kept here because it is driven by `selectedMethod` which this hook owns.
  // ---------------------------------------------------------------------------
  const panelContentRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);

  useEffect(() => {
    if (!selectedMethod) {
      setPanelHeight(0);
      return undefined;
    }
    const el = panelContentRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setPanelHeight(entry.target.scrollHeight);
    });
    ro.observe(el);
    // Measure immediately in case content is already laid out.
    setPanelHeight(el.scrollHeight);
    return () => ro.disconnect();
  }, [selectedMethod]);

  // Once a flow is underway (SMS code sent or TOTP enrolling), lock method
  // switching so the user cannot change method mid-flow and lose progress.
  const isMethodLocked = isCodeSent || isTotpEnrolling;

  // ---------------------------------------------------------------------------
  // Analytics helpers — preserve existing event names via `eventPrefix`.
  // ---------------------------------------------------------------------------
  const emit = useCallback(
    (name, extra = {}) => {
      try {
        logEvent(analytics, `${eventPrefix}${name}`, { accountType, userType, ...extra });
      } catch (_) {
        // Analytics must never break enrollment.
      }
    },
    [eventPrefix, accountType, userType]
  );

  const logMfaFailure = useCallback(
    (stage, method, err) => {
      if (!logFailures) return;
      emit('mfa_enrolment_failed', { stage, method, code: err?.code || 'unknown' });
    },
    [logFailures, emit]
  );

  // ---------------------------------------------------------------------------
  // reCAPTCHA teardown — deferred to avoid a race with Google's internal
  // cleanup (clearing synchronously right after render can throw). Stable ref so
  // memoised handlers can depend on it without re-creating each render.
  // ---------------------------------------------------------------------------
  const deferClearRecaptcha = useCallback(() => {
    if (!recaptchaVerifierRef.current) return;
    const toClear = recaptchaVerifierRef.current;
    recaptchaVerifierRef.current = null;
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          toClear.clear();
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[MFA] recaptcha clear error (safe to ignore):', e);
          }
        }
      }, 0);
    });
  }, []);

  // Initialise the invisible reCAPTCHA only on the SMS path. TOTP needs none,
  // and the 'send-code-button' element it binds to only exists in the SMS view —
  // binding before the user picks SMS would target a missing element.
  useEffect(() => {
    if (selectedMethod !== 'sms') return undefined;
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'send-code-button', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          deferClearRecaptcha();
        },
      });
    }
    return () => {
      if (recaptchaVerifierRef.current) {
        deferClearRecaptcha();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod]);

  // Cooldown ticker for the SMS resend button.
  useEffect(() => {
    const tick = () => {
      if (sentAt) {
        const elapsed = Math.floor((Date.now() - sentAt) / 1000);
        setResendSecondsLeft(Math.max(0, cooldownSeconds - elapsed));
      } else {
        setResendSecondsLeft(0);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sentAt]);

  // ---------------------------------------------------------------------------
  // Shared Firestore persistence for a successful enrollment.
  // ---------------------------------------------------------------------------
  // Writes the standard MFA status + audit trail to whichever document the
  // caller resolves. Uses updateDoc when the doc already exists and setDoc(merge)
  // otherwise, so it is safe for both provider docs (which always exist) and
  // service-user docs (which may not yet). Best-effort: failures are logged, not
  // thrown, because the Auth-level enrollment (the security-critical step) has
  // already succeeded and must not trap the user on this page.
  const persistEnrollment = useCallback(
    async ({ method, action, source }) => {
      try {
        const resolved = await resolveDocRef();
        if (!resolved) return;
        const { ref, snap } = resolved;
        const exists = typeof snap?.exists === 'function' ? snap.exists() : Boolean(snap);
        const normalizedPhone = method === 'phone' ? formatUKPhoneNumber(phoneNumber) : undefined;

        const updates = {
          isMFASetUp: true,
          mfaMethod: method,
          mfaAuditTrail: arrayUnion({
            action,
            source,
            method,
            at: new Date(),
            ...(normalizedPhone ? { phoneNumber: normalizedPhone } : {}),
          }),
        };
        if (normalizedPhone) updates.mfaPhoneNumber = normalizedPhone;
        // Only stamp the first-ever setup time once.
        if (!exists || !snap.data()?.mfaSetupAt) {
          updates.mfaSetupAt = serverTimestamp();
        }

        if (exists) {
          await updateDoc(ref, updates);
        } else {
          await setDoc(ref, updates, { merge: true });
        }
      } catch (firestoreError) {
        console.error('[MFA] Error persisting enrollment status:', firestoreError);
        // Do not block navigation — Auth enrollment already succeeded.
      }
    },
    [resolveDocRef, phoneNumber]
  );

  // Confirm an enrolled factor actually exists after an "already in use" error.
  // Firebase can report a factor as already-in-use in edge cases where it is not
  // truly enrolled; we only proceed to completion if a factor is really present.
  const confirmFactorEnrolled = useCallback(async () => {
    try {
      const user = auth.currentUser;
      await user?.getIdToken(true);
      await user?.reload?.();
      return (multiFactor(user)?.enrolledFactors || []).length > 0;
    } catch (_) {
      return false;
    }
  }, []);

  // ===========================================================================
  // TOTP (authenticator app) enrollment
  // ===========================================================================
  const handleGenerateTotpSecret = useCallback(async () => {
    if (isTotpGenerating || isTotpReady) return;
    try {
      setIsTotpGenerating(true);
      setIsTotpError(false);
      setError('');
      setErrorCode('');

      const user = auth.currentUser;
      if (!user) {
        setError('Please sign in again.');
        return;
      }

      // MFA session for the current user — used to mint a TOTP secret. No
      // reCAPTCHA is involved (part of why authenticator apps are more reliable).
      const multiFactorSession = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      const uri = secret.generateQrCodeUrl(user.email, 'ThinkDivergent');

      setTotpSecret(secret);
      setTotpUri(uri);
      setTotpSecretKey(secret.secretKey);
      setIsTotpReady(true);
      emit('mfa_totp_secret_generated', {});
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[MFA][TOTP] Error generating secret:', err);
      }
      let errorMessage = 'Failed to generate authenticator setup. ';
      if (err?.code === 'auth/requires-recent-login') {
        errorMessage += 'Your session has expired. Please sign in again.';
      } else if (err?.code === 'auth/network-request-failed') {
        errorMessage += 'Network error. Check your connection and try again.';
      } else {
        errorMessage += 'Please try again.';
      }
      setError(errorMessage);
      setErrorCode(err?.code || 'unknown');
      setIsTotpError(true);
      logMfaFailure('generate_secret', 'totp', err);
    } finally {
      setIsTotpGenerating(false);
    }
  }, [isTotpGenerating, isTotpReady, emit, logMfaFailure]);

  // Auto-generate the TOTP secret as soon as the authenticator method is active,
  // so the QR code starts loading without an extra click. The guards prevent
  // duplicate runs and a retry loop after a generation failure.
  useEffect(() => {
    if (selectedMethod === 'totp' && !isTotpReady && !isTotpGenerating && !isTotpError) {
      handleGenerateTotpSecret();
    }
  }, [selectedMethod, isTotpReady, isTotpGenerating, isTotpError, handleGenerateTotpSecret]);

  const handleVerifyTotpCode = useCallback(async () => {
    if (!totpSecret || isTotpEnrolling) return;
    try {
      setIsTotpEnrolling(true);
      setError('');
      setErrorCode('');

      const user = auth.currentUser;
      if (!user) {
        setError('Please sign in again.');
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
      await multiFactor(user).enroll(assertion, 'Authenticator App');
      // Force token refresh so downstream checks (ProtectedRoute) see the factor.
      await user.getIdToken(true);

      await persistEnrollment({ method: 'totp', action: 'mfa_enrolled', source: 'setup_page' });
      emit('mfa_setup_complete', { method: 'totp' });
      await onEnrolled();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[MFA][TOTP] Error verifying code:', err?.code, err?.message, err);
      }
      if (err?.code === 'auth/invalid-verification-code' || err?.code === 'auth/code-expired') {
        setError('The code is incorrect or expired. Please enter the latest code from your authenticator app.');
        setErrorCode(err.code);
        logMfaFailure('verify_code', 'totp', err);
      } else if (err?.code === 'auth/second-factor-already-in-use') {
        // TOTP factor already enrolled — only proceed if it truly exists.
        const hasFactor = await confirmFactorEnrolled();
        if (!hasFactor) {
          setError('We could not complete 2-step verification. Please try again.');
          setErrorCode('MS-008');
          logMfaFailure('verify_code', 'totp', { code: 'MS-008' });
          return;
        }
        await persistEnrollment({ method: 'totp', action: 'mfa_enrolled_already_present', source: 'setup_page' });
        emit('mfa_setup_complete_already_enrolled', { method: 'totp', confirmed: true });
        await onEnrolled();
      } else if (
        err?.code === 'auth/requires-recent-login' ||
        err?.code === 'auth/user-token-expired' ||
        err?.code === 'auth/user-disabled'
      ) {
        setError('Your session has expired. Please sign in again.');
        setErrorCode(err.code);
        logMfaFailure('verify_code', 'totp', err);
      } else {
        setError('We could not verify the code. Please try again.');
        setErrorCode(err?.code || 'unknown');
        logMfaFailure('verify_code', 'totp', err);
      }
    } finally {
      setIsTotpEnrolling(false);
    }
  }, [totpSecret, isTotpEnrolling, verificationCode, persistEnrollment, emit, onEnrolled, logMfaFailure, confirmFactorEnrolled]);

  const handleCopySecret = useCallback(async () => {
    if (!totpSecretKey) return;
    try {
      await navigator.clipboard.writeText(totpSecretKey);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (_) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[MFA][TOTP] Clipboard API not available');
      }
    }
  }, [totpSecretKey]);

  // Retry after a generation failure (the page's "Try again" button).
  const handleRetryTotp = useCallback(() => {
    setIsTotpError(false);
    handleGenerateTotpSecret();
  }, [handleGenerateTotpSecret]);

  // ===========================================================================
  // SMS enrollment
  // ===========================================================================
  const handlePhoneChange = useCallback((value) => {
    // Allow only digits and spaces for a clean UX; we normalise on submit.
    const cleaned = (value || '').replace(/[^0-9\s]/g, '');
    setPhoneNumber(cleaned);
    setIsPhoneValid(isValidUKPhone(cleaned));
  }, []);

  const handleSendCode = useCallback(async () => {
    if (isSending) return; // prevent double-submission while in-flight
    try {
      setIsSending(true);
      const user = auth.currentUser;
      if (!user) {
        setError('Please sign in again.');
        return;
      }

      // Recreate a fresh reCAPTCHA verifier for each send attempt.
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'send-code-button', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          deferClearRecaptcha();
        },
      });
      await recaptchaVerifierRef.current.render();

      const multiFactorSession = await multiFactor(user).getSession();
      const formattedPhoneNumber = formatUKPhoneNumber(phoneNumber);
      const phoneInfoOptions = { phoneNumber: formattedPhoneNumber, session: multiFactorSession };
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifierRef.current
      );

      setVerificationId(newVerificationId);
      setIsCodeSent(true);
      setError('');
      setErrorCode('');
      setSentAt(Date.now());
      try {
        setLast4((formattedPhoneNumber.replace(/\D/g, '') || '').slice(-4));
      } catch (_) {}
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[MFA][SMS] Error sending verification code:', err?.code, err?.message, err);
      }
      let errorMessage = 'Failed to send verification code. ';
      switch (err?.code) {
        case 'auth/invalid-phone-number':
          errorMessage += 'Please enter a valid phone number.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage += 'SMS authentication is not enabled for this project.';
          break;
        case 'auth/quota-exceeded':
          errorMessage += 'SMS quota exceeded. Please try again later.';
          break;
        case 'auth/invalid-app-credential':
          errorMessage += 'reCAPTCHA validation failed. Please try again or refresh the page.';
          break;
        default:
          errorMessage += 'Please try again.';
      }
      setError(errorMessage);
      setErrorCode(err?.code || 'unknown');
      logMfaFailure('send_code', 'phone', err);
      if (recaptchaVerifierRef.current) deferClearRecaptcha();
    } finally {
      setIsSending(false);
    }
  }, [isSending, phoneNumber, deferClearRecaptcha, logMfaFailure]);

  const handleResendCode = useCallback(async () => {
    if (isResending || resendSecondsLeft > 0) return;
    try {
      setIsResending(true);
      const user = auth.currentUser;
      if (!user) {
        setError('Please sign in again.');
        return;
      }
      // Bind a fresh verifier to the visible Resend button in the code view.
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'resend-code-button', {
        size: 'invisible',
        callback: () => {},
        'expired-callback': () => {
          deferClearRecaptcha();
        },
      });
      await recaptchaVerifierRef.current.render();

      const session = await multiFactor(user).getSession();
      const formattedPhoneNumber = formatUKPhoneNumber(phoneNumber);
      const phoneInfoOptions = { phoneNumber: formattedPhoneNumber, session };
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        recaptchaVerifierRef.current
      );
      setVerificationId(newVerificationId);
      setError('');
      setErrorCode('');
      setSentAt(Date.now());
      setResendJustSent(true);
      setTimeout(() => setResendJustSent(false), 1500);
      try {
        setLast4((formattedPhoneNumber.replace(/\D/g, '') || '').slice(-4));
      } catch (_) {}
    } catch (err) {
      // Friendly mappings for common resend failures.
      if (err?.code === 'auth/argument-error' || err?.code === 'auth/invalid-app-credential') {
        setError('reCAPTCHA validation was missing or expired. Please try again.');
        setErrorCode('MS-006');
      } else if (err?.code === 'auth/network-request-failed') {
        setError('Network error. Check your connection and try again.');
        setErrorCode('auth/network-request-failed');
      } else if (err?.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment before trying again.');
        setErrorCode('auth/too-many-requests');
      } else if (err?.code === 'auth/quota-exceeded') {
        setError('SMS quota exceeded. Please try again later.');
        setErrorCode('auth/quota-exceeded');
      } else {
        // One safe retry with a fresh verifier/session to ride out transient races.
        try {
          if (recaptchaVerifierRef.current) {
            try {
              recaptchaVerifierRef.current.clear();
            } catch (_) {}
            recaptchaVerifierRef.current = null;
          }
          recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'resend-code-button', {
            size: 'invisible',
            callback: () => {},
          });
          await recaptchaVerifierRef.current.render();
          const user = auth.currentUser;
          const session = await multiFactor(user).getSession();
          const formattedPhoneNumber = formatUKPhoneNumber(phoneNumber);
          const phoneInfoOptions = { phoneNumber: formattedPhoneNumber, session };
          const phoneAuthProvider = new PhoneAuthProvider(auth);
          const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            recaptchaVerifierRef.current
          );
          setVerificationId(newVerificationId);
          setError('');
          setErrorCode('');
          setSentAt(Date.now());
          setResendJustSent(true);
          setTimeout(() => setResendJustSent(false), 1500);
          try {
            setLast4((formattedPhoneNumber.replace(/\D/g, '') || '').slice(-4));
          } catch (_) {}
          return;
        } catch (retryErr) {
          setError('Could not resend the code. Please try again shortly.');
          setErrorCode(retryErr?.code || err?.code || 'unknown');
          if (process.env.NODE_ENV === 'development') {
            console.warn('[MFA][SMS][resend] unknown error object:', err, 'retry:', retryErr);
          }
        }
      }
    } finally {
      setIsResending(false);
    }
  }, [isResending, resendSecondsLeft, phoneNumber, deferClearRecaptcha]);

  const handleVerifySms = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!verificationId) {
        setError('Please request a new verification code.');
        setErrorCode('auth/missing-verification-id');
        return;
      }

      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, 'Phone Number');
      await user.getIdToken(true);

      await persistEnrollment({ method: 'phone', action: 'mfa_enrolled', source: 'setup_page' });

      // Clean up reCAPTCHA BEFORE navigating away.
      if (recaptchaVerifierRef.current) deferClearRecaptcha();
      emit('mfa_setup_complete', { method: 'phone' });
      await onEnrolled();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[MFA][SMS] Error verifying code:', err?.code, err?.message, err);
      }
      if (err?.code === 'auth/invalid-verification-code' || err?.code === 'auth/code-expired') {
        setError('The code is incorrect or expired.');
        setErrorCode(err.code);
        logMfaFailure('verify_code', 'phone', err);
      } else if (err?.code === 'auth/credential-already-in-use') {
        // The phone number already belongs to (is enrolled on) an account.
        if (!isNonProdEnvironment()) {
          // Production (incl. production-slam-nhs): surface a clear conflict.
          setError('This phone number is already used by another account. Please use a different number.');
          setErrorCode('MS-007');
          emit('mfa_enroll_conflict', { env: 'prod' });
          logMfaFailure('verify_code', 'phone', { code: 'MS-007' });
          if (recaptchaVerifierRef.current) deferClearRecaptcha();
          return;
        }
        // Dev/staging: shared test numbers are reused across accounts. Confirm
        // whether a factor is actually enrolled; if not, bypass for test
        // convenience so testers are not blocked by the shared number.
        const envLabel = import.meta.env.VITE_ENVIRONMENT || process.env.NODE_ENV;
        const hasFactor = await confirmFactorEnrolled();
        if (!hasFactor) {
          console.warn('[MFA][BYPASS_DEV] credential-already-in-use without verified enrollment; continuing for test convenience');
          emit('mfa_enroll_conflict_bypass', { env: envLabel });
          await persistEnrollment({ method: 'phone', action: 'mfa_enrolled', source: 'setup_page_conflict_bypass' });
        } else {
          emit('mfa_enroll_already_in_use_checked', { hasFactor: true, env: envLabel });
          await persistEnrollment({ method: 'phone', action: 'mfa_enrolled', source: 'setup_page' });
        }
        if (recaptchaVerifierRef.current) deferClearRecaptcha();
        setError('Using shared test numbers; continuing');
        setErrorCode('DEV-BYPASS');
        await onEnrolled();
      } else if (err?.code === 'auth/second-factor-already-in-use') {
        // Factor already added. Confirm enrollment exists before proceeding.
        const hasFactor = await confirmFactorEnrolled();
        if (!hasFactor) {
          setError('We could not complete 2-step verification. Please try again.');
          setErrorCode('MS-008');
          logMfaFailure('verify_code', 'phone', { code: 'MS-008' });
          if (recaptchaVerifierRef.current) deferClearRecaptcha();
          return;
        }
        await persistEnrollment({ method: 'phone', action: 'mfa_enrolled_already_present', source: 'setup_page' });
        if (recaptchaVerifierRef.current) deferClearRecaptcha();
        emit('mfa_setup_complete_already_enrolled', { method: 'phone', confirmed: true });
        await onEnrolled();
      } else if (
        err?.code === 'auth/user-token-expired' ||
        err?.code === 'auth/user-disabled' ||
        err?.code === 'auth/requires-recent-login'
      ) {
        setError('Your session has expired. Please sign in again.');
        setErrorCode(err.code);
        logMfaFailure('verify_code', 'phone', err);
      } else {
        setError('We could not verify the code. Please try again.');
        setErrorCode(err?.code || 'unknown');
        logMfaFailure('verify_code', 'phone', err);
      }
    }
  }, [verificationId, verificationCode, persistEnrollment, deferClearRecaptcha, emit, onEnrolled, logMfaFailure, confirmFactorEnrolled]);

  // ===========================================================================
  // Method selection / switching
  // ===========================================================================
  // Reset the state of the method we are leaving so the user starts the new one
  // fresh. Used both by the method picker (null -> method) and any in-place
  // switch. SMS is only selectable when `allowSms` is true.
  const handleSelectMethod = useCallback(
    (method) => {
      if (method === selectedMethod) return;
      if (method === 'sms' && !allowSms) return;
      setError('');
      setErrorCode('');
      setVerificationCode('');
      if (method === 'totp') {
        // Leaving SMS — tear down reCAPTCHA and reset the code-sent state.
        if (recaptchaVerifierRef.current) deferClearRecaptcha();
        setIsCodeSent(false);
        setSentAt(0);
      } else {
        // Leaving TOTP — reset authenticator state.
        setTotpSecret(null);
        setTotpUri('');
        setTotpSecretKey('');
        setIsTotpReady(false);
        setIsTotpError(false);
        setCopiedSecret(false);
      }
      setSelectedMethod(method);
    },
    [selectedMethod, allowSms, deferClearRecaptcha]
  );

  // Return to the method-pick step (selectedMethod = null), clearing any
  // in-progress state for both methods. Disabled once a flow is locked.
  const handleChangeMethod = useCallback(() => {
    if (isMethodLocked) return;
    setError('');
    setErrorCode('');
    setVerificationCode('');
    if (recaptchaVerifierRef.current) deferClearRecaptcha();
    setIsCodeSent(false);
    setSentAt(0);
    setTotpSecret(null);
    setTotpUri('');
    setTotpSecretKey('');
    setIsTotpReady(false);
    setIsTotpError(false);
    setCopiedSecret(false);
    setSelectedMethod(null);
    setPanelHeight(0);
  }, [isMethodLocked, deferClearRecaptcha]);

  // Exposed so pages can tear down reCAPTCHA before sign-out.
  const cleanupRecaptcha = useCallback(() => {
    if (recaptchaVerifierRef.current) deferClearRecaptcha();
  }, [deferClearRecaptcha]);

  return {
    // selection
    selectedMethod,
    handleSelectMethod,
    handleChangeMethod,
    isMethodLocked,
    // shared
    error,
    errorCode,
    verificationCode,
    setVerificationCode,
    panelContentRef,
    panelHeight,
    cleanupRecaptcha,
    // SMS
    phoneNumber,
    isPhoneValid,
    handlePhoneChange,
    isCodeSent,
    isSending,
    handleSendCode,
    handleResendCode,
    handleVerifySms,
    resendSecondsLeft,
    isResending,
    resendJustSent,
    last4,
    // TOTP
    totpUri,
    totpSecretKey,
    isTotpReady,
    isTotpError,
    isTotpEnrolling,
    copiedSecret,
    handleGenerateTotpSecret,
    handleVerifyTotpCode,
    handleCopySecret,
    handleRetryTotp,
  };
}
